import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { 
  AlertTriangle, Heart, Phone, User, Pill, 
  Hospital, Activity, Shield, Clock, Edit
} from 'lucide-react';

// Color themes matching the create page
const COLOR_THEMES = {
  red: { 
    bg: 'bg-gradient-to-b from-[#D92B2B] to-[#C02424]',
    accent: 'bg-[#D92B2B]',
    headerBg: 'bg-[#A91F1F]/50',
    textMuted: 'text-red-200',
    cardBg: 'bg-white',
    cardText: 'text-slate-800',
    cardTextMuted: 'text-slate-600',
    cardBorder: 'border-slate-200',
    badgeBg: 'bg-slate-100',
    badgeText: 'text-slate-800'
  },
  black: { 
    bg: 'bg-gradient-to-b from-slate-900 to-black',
    accent: 'bg-slate-700',
    headerBg: 'bg-black/50',
    textMuted: 'text-slate-400',
    cardBg: 'bg-slate-800',
    cardText: 'text-white',
    cardTextMuted: 'text-slate-300',
    cardBorder: 'border-slate-700',
    badgeBg: 'bg-slate-700',
    badgeText: 'text-white'
  },
  blue: { 
    bg: 'bg-gradient-to-b from-blue-600 to-blue-800',
    accent: 'bg-blue-600',
    headerBg: 'bg-blue-900/50',
    textMuted: 'text-blue-200',
    cardBg: 'bg-white',
    cardText: 'text-slate-800',
    cardTextMuted: 'text-slate-600',
    cardBorder: 'border-slate-200',
    badgeBg: 'bg-slate-100',
    badgeText: 'text-slate-800'
  },
  green: { 
    bg: 'bg-gradient-to-b from-emerald-600 to-emerald-800',
    accent: 'bg-emerald-600',
    headerBg: 'bg-emerald-900/50',
    textMuted: 'text-emerald-200',
    cardBg: 'bg-white',
    cardText: 'text-slate-800',
    cardTextMuted: 'text-slate-600',
    cardBorder: 'border-slate-200',
    badgeBg: 'bg-slate-100',
    badgeText: 'text-slate-800'
  }
};

const EmergencyCardViewer = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [cardData, setCardData] = useState(null);
  const [theme, setTheme] = useState(COLOR_THEMES.red);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    try {
      const encodedData = searchParams.get('d');
      
      if (!encodedData) {
        setError('No emergency data found in QR code');
        setLoading(false);
        return;
      }
      
      // Decode base64 data
      const jsonStr = decodeURIComponent(escape(atob(encodedData)));
      const data = JSON.parse(jsonStr);
      
      // Expand compressed data
      const expanded = {
        full_name: data.n || 'Unknown',
        date_of_birth: data.d || '',
        blood_group: data.b || '',
        gender: data.g || '',
        allergies: (data.a || []).map(a => ({
          name: a.n,
          severity: a.s === 's' ? 'severe' : a.s === 'm' ? 'moderate' : 'mild'
        })),
        critical_conditions: data.c || [],
        current_medications: data.m || [],
        previous_surgeries: (data.su || []).map(s => ({
          name: s.n,
          year: s.y
        })),
        emergency_contacts: (data.e || []).map(c => ({
          name: c.n,
          relationship: c.r,
          phone: c.p,
          is_primary: c.i === 1
        })),
        organ_donor: data.o === 1,
        preferred_hospital: data.h || '',
        doctor_name: data.dn || '',
        doctor_phone: data.dp || '',
        special_instructions: data.s || '',
        color_theme: data.th || 'red',
        created_at: data.t ? new Date(data.t).toISOString() : null
      };
      
      // Set the color theme
      setTheme(COLOR_THEMES[expanded.color_theme] || COLOR_THEMES.red);
      setCardData(expanded);
      setLoading(false);
    } catch (err) {
      console.error('Decode error:', err);
      setError('Invalid QR code data');
      setLoading(false);
    }
  }, [searchParams]);

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'severe': return 'bg-red-100 text-[#A91F1F] border-red-300';
      case 'moderate': return 'bg-orange-100 text-orange-800 border-orange-300';
      case 'mild': return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      default: return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  // Calculate age from date of birth
  const calculateAge = (dob) => {
    if (!dob) return null;
    const parts = dob.split('/');
    if (parts.length !== 3) return null;
    const birthDate = new Date(parts[2], parts[0] - 1, parts[1]); // MM/DD/YYYY
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#D92B2B] to-[#C02424] flex items-center justify-center">
        <div className="text-white text-center">
          <div className="animate-spin w-12 h-12 border-4 border-white border-t-transparent rounded-full mx-auto mb-4" />
          <p>Loading Emergency Information...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#D92B2B] to-[#C02424] flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-6 text-center">
            <AlertTriangle className="h-16 w-16 text-[#E63333] mx-auto mb-4" />
            <h1 className="text-xl font-bold text-slate-800 mb-2">Unable to Load</h1>
            <p className="text-slate-600">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${theme.bg}`}>
      {/* Header */}
      <header className={`${theme.headerBg} backdrop-blur px-4 py-3 sticky top-0 z-40`}>
        <div className="max-w-lg mx-auto flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Heart className="h-6 w-6 text-white" />
            <div>
              <h1 className="text-lg font-bold text-white">Emergency Card</h1>
              <p className={`${theme.textMuted} text-xs`}>Medical Information</p>
            </div>
          </div>
          <div className="flex items-center gap-1 text-white/60 text-xs">
            <Shield className="h-3 w-3" />
            <span>Own Your Data</span>
          </div>
        </div>
      </header>

      <main className="max-w-lg mx-auto p-4 pb-8">
        {/* Critical Info Card */}
        <Card className={`mb-4 border-2 shadow-lg ${theme.cardBg} ${theme.cardBorder}`}>
          <CardContent className="p-4">
            <div className="flex items-start gap-4">
              {/* Blood Group - Large on the side */}
              {cardData.blood_group && (
                <div className={`flex flex-col items-center justify-center p-3 rounded-xl ${cardData.color_theme === 'black' ? 'bg-red-900' : 'bg-[#D92B2B]'}`}>
                  <Heart className="h-6 w-6 text-white mb-1" />
                  <span className="text-white text-2xl font-bold">{cardData.blood_group}</span>
                </div>
              )}
              <div className="flex-1">
                <h2 className={`text-2xl font-bold ${theme.cardText}`}>{cardData.full_name}</h2>
                {/* Age badge */}
                <div className="flex items-center gap-2 mt-2">
                  {calculateAge(cardData.date_of_birth) !== null && (
                    <Badge className="bg-blue-600 text-white text-lg px-3 py-1">
                      {calculateAge(cardData.date_of_birth)} yrs
                    </Badge>
                  )}
                  {cardData.organ_donor && (
                    <Badge className="bg-green-600 text-white">
                      Organ Donor
                    </Badge>
                  )}
                </div>
                {/* DOB and Gender together */}
                <div className={`flex items-center gap-3 mt-2 text-sm ${theme.cardTextMuted}`}>
                  {cardData.date_of_birth && (
                    <span>DOB: {cardData.date_of_birth}</span>
                  )}
                  {cardData.gender && cardData.date_of_birth && <span>•</span>}
                  {cardData.gender && (
                    <span className="capitalize">{cardData.gender}</span>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Allergies - CRITICAL */}
        {cardData.allergies.length > 0 && (
          <Card className="mb-4 border-red-300 bg-red-50">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-3">
                <AlertTriangle className="h-5 w-5 text-[#D92B2B]" />
                <h3 className="font-bold text-[#A91F1F]">ALLERGIES</h3>
              </div>
              <div className="flex flex-wrap gap-2">
                {cardData.allergies.map((allergy, idx) => (
                  <Badge 
                    key={idx} 
                    className={`${getSeverityColor(allergy.severity)} border text-sm px-3 py-1`}
                  >
                    {allergy.name}
                    <span className="ml-1 opacity-70 text-xs">
                      ({allergy.severity})
                    </span>
                  </Badge>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Medical Conditions */}
        {cardData.critical_conditions.length > 0 && (
          <Card className={`mb-4 ${theme.cardBg} ${theme.cardBorder}`}>
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-3">
                <Activity className={`h-5 w-5 ${cardData.color_theme === 'black' ? 'text-orange-400' : 'text-orange-600'}`} />
                <h3 className={`font-bold ${theme.cardText}`}>Medical Conditions</h3>
              </div>
              <div className="flex flex-wrap gap-2">
                {cardData.critical_conditions.map((condition, idx) => (
                  <Badge key={idx} className={`text-sm ${theme.badgeBg} ${theme.badgeText}`}>
                    {condition}
                  </Badge>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Medications */}
        {cardData.current_medications.length > 0 && (
          <Card className={`mb-4 ${theme.cardBg} ${theme.cardBorder}`}>
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-3">
                <Pill className={`h-5 w-5 ${cardData.color_theme === 'black' ? 'text-blue-400' : 'text-blue-600'}`} />
                <h3 className={`font-bold ${theme.cardText}`}>Current Medications</h3>
              </div>
              <div className="flex flex-wrap gap-2">
                {cardData.current_medications.map((med, idx) => (
                  <Badge key={idx} className={`text-sm ${theme.badgeBg} ${theme.badgeText}`}>
                    {med}
                  </Badge>
                ))}
              </div>
              <p className={`text-xs mt-3 italic ${theme.cardTextMuted}`}>
                Medication names may vary by region/country
              </p>
            </CardContent>
          </Card>
        )}

        {/* Previous Surgeries */}
        {cardData.previous_surgeries && cardData.previous_surgeries.length > 0 && (
          <Card className={`mb-4 ${theme.cardBg} ${theme.cardBorder}`}>
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-3">
                <Hospital className={`h-5 w-5 ${cardData.color_theme === 'black' ? 'text-purple-400' : 'text-purple-600'}`} />
                <h3 className={`font-bold ${theme.cardText}`}>Previous Surgeries</h3>
              </div>
              <div className="flex flex-wrap gap-2">
                {cardData.previous_surgeries.map((surgery, idx) => (
                  <Badge key={idx} className={`text-sm ${cardData.color_theme === 'black' ? 'bg-purple-900 text-purple-200' : 'bg-purple-100 text-purple-800'}`}>
                    {surgery.name}{surgery.year ? ` (${surgery.year})` : ''}
                  </Badge>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Emergency Contacts */}
        {cardData.emergency_contacts.length > 0 && (
          <Card className={`mb-4 ${theme.cardBg} ${theme.cardBorder}`}>
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-3">
                <Phone className={`h-5 w-5 ${cardData.color_theme === 'black' ? 'text-green-400' : 'text-green-600'}`} />
                <h3 className={`font-bold ${theme.cardText}`}>Emergency Contacts</h3>
              </div>
              <div className="space-y-3">
                {cardData.emergency_contacts.map((contact, idx) => (
                  <div key={idx} className={`flex items-center justify-between p-3 rounded-lg ${cardData.color_theme === 'black' ? 'bg-slate-700' : 'bg-slate-50'}`}>
                    <div>
                      <p className={`font-medium ${theme.cardText}`}>{contact.name}</p>
                      <p className={`text-sm ${theme.cardTextMuted}`}>{contact.relationship}</p>
                    </div>
                    <a 
                      href={`tel:${contact.phone}`}
                      className="flex items-center gap-2 bg-green-600 text-white px-4 py-2 rounded-lg font-medium"
                    >
                      <Phone className="h-4 w-4" />
                      Call
                    </a>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Doctor Info */}
        {(cardData.doctor_name || cardData.preferred_hospital) && (
          <Card className={`mb-4 ${theme.cardBg} ${theme.cardBorder}`}>
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-3">
                <Hospital className={`h-5 w-5 ${cardData.color_theme === 'black' ? 'text-purple-400' : 'text-purple-600'}`} />
                <h3 className={`font-bold ${theme.cardText}`}>Healthcare Provider</h3>
              </div>
              {cardData.doctor_name && (
                <div className={`flex items-center justify-between p-3 rounded-lg mb-2 ${cardData.color_theme === 'black' ? 'bg-slate-700' : 'bg-slate-50'}`}>
                  <div>
                    <p className={`font-medium ${theme.cardText}`}>{cardData.doctor_name}</p>
                    <p className={`text-sm ${theme.cardTextMuted}`}>Primary Doctor</p>
                  </div>
                  {cardData.doctor_phone && (
                    <a 
                      href={`tel:${cardData.doctor_phone}`}
                      className="flex items-center gap-2 bg-purple-600 text-white px-4 py-2 rounded-lg font-medium"
                    >
                      <Phone className="h-4 w-4" />
                      Call
                    </a>
                  )}
                </div>
              )}
              {cardData.preferred_hospital && (
                <p className={theme.cardTextMuted}>
                  <strong className={theme.cardText}>Preferred Hospital:</strong> {cardData.preferred_hospital}
                </p>
              )}
            </CardContent>
          </Card>
        )}

        {/* Special Instructions */}
        {cardData.special_instructions && (
          <Card className={`mb-4 ${cardData.color_theme === 'black' ? 'border-amber-600 bg-amber-900/30' : 'border-amber-300 bg-amber-50'}`}>
            <CardContent className="p-4">
              <h3 className={`font-bold mb-2 ${cardData.color_theme === 'black' ? 'text-amber-400' : 'text-amber-800'}`}>Special Instructions</h3>
              <p className={cardData.color_theme === 'black' ? 'text-amber-200' : 'text-amber-900'}>{cardData.special_instructions}</p>
            </CardContent>
          </Card>
        )}

        {/* Footer */}
        <div className="text-center mt-6 text-white/60 text-xs">
          <div className="flex items-center justify-center gap-1 mb-1">
            <Clock className="h-3 w-3" />
            {cardData.created_at && (
              <span>Created: {new Date(cardData.created_at).toLocaleDateString()}</span>
            )}
          </div>
          <p className="flex items-center justify-center gap-1">
            <Shield className="h-3 w-3" />
            Data stored locally - Own Your Data
          </p>
          <p className="mt-2">Powered by Emergency Card</p>
        </div>
      </main>
    </div>
  );
};

export default EmergencyCardViewer;
